import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import type { Carate, Carecommendation } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  NEW_PRICE_LOWER_LIMIT_FACTOR,
  NEW_PRICE_UPPER_LIMIT_FACTOR,
  NUMBER_INPUT_MAX_VALUE,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { timeout } from 'lumen/utils/waiters';

interface HotelPricingAssistantEditPriceNumberInputSignature {
  Args: {
    carateObject: Carate;
    newPriceInputFieldErrorState: { errorMessage?: string; hasErrors: boolean };
    newPriceValue: number | null;
    recommendationObject?: Carecommendation | null;
    updateNewPrice: (value: number | null) => void;
  };
}

export default class HotelPricingAssistantEditPriceNumberInput extends Component<HotelPricingAssistantEditPriceNumberInputSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  debouncedNewPriceValueTask = task(
    { restartable: true },
    async (value): Promise<void> => {
      await timeout();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.updateNewPrice(value);
    },
  );

  get newPriceInputFieldIsTouched(): boolean {
    return Boolean(this.args.newPriceInputFieldErrorState.errorMessage);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get newPriceInputFieldWarningState() {
    if (this.args.newPriceInputFieldErrorState.hasErrors) {
      return {
        warningMessage: '',
        hasWarnings: false,
      };
    }

    if (this.args.newPriceValue) {
      // New price is lower than min price defined in settings
      if (
        this.args.carateObject.userSetMinimum &&
        this.args.newPriceValue < this.args.carateObject.userSetMinimum
      ) {
        return {
          warningMessage: this.intl.t(
            'pricing_assistant.edit_price.warning.lower_than_settings',
            {
              minSettingsPrice: this.hotelAndMarketService.formatRate(
                this.args.carateObject.userSetMinimum,
              ),
              htmlSafe: true,
            },
          ),
          hasWarnings: true,
        };
      }

      // New price is higher than max price defined in settings
      if (
        this.args.carateObject.userSetMaximum &&
        this.args.newPriceValue > this.args.carateObject.userSetMaximum
      ) {
        return {
          warningMessage: this.intl.t(
            'pricing_assistant.edit_price.warning.higher_than_settings',
            {
              maxSettingsPrice: this.hotelAndMarketService.formatRate(
                this.args.carateObject.userSetMaximum,
              ),
              htmlSafe: true,
            },
          ),
          hasWarnings: true,
        };
      }

      // Case 1: Day with recommendation: Check against recommended price
      // Case 2: Day without recommendation: Check against current price
      const priceToCheck = this.args.recommendationObject
        ? this.args.recommendationObject.recommendedValue
        : this.args.carateObject.value;

      // New price is lower than usual
      if (
        this.args.newPriceValue <
        priceToCheck * NEW_PRICE_LOWER_LIMIT_FACTOR
      ) {
        return {
          warningMessage: this.intl.t(
            'pricing_assistant.edit_price.warning.low',
          ),
          hasWarnings: true,
        };
      }

      // New price is higher than usual
      if (
        this.args.newPriceValue >
        priceToCheck * NEW_PRICE_UPPER_LIMIT_FACTOR
      ) {
        return {
          warningMessage: this.intl.t(
            'pricing_assistant.edit_price.warning.high',
          ),
          hasWarnings: true,
        };
      }
    }
    return {
      warningMessage: '',
      hasWarnings: false,
    };
  }

  @action
  updateNewPrice(value: number | null): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.debouncedNewPriceValueTask.perform(value);
  }

  <template>
    <div class="flex items-center">
      <PrismNumberInput
        @autofocus={{true}}
        @disabled={{or
          this.access.isPricingAssistantActionOrInputDisabled
          @recommendationObject.isBeingPushedOrIsPushed
        }}
        @errorMessage={{@newPriceInputFieldErrorState.errorMessage}}
        @hasErrors={{@newPriceInputFieldErrorState.hasErrors}}
        @hasWarnings={{this.newPriceInputFieldWarningState.hasWarnings}}
        @isTouched={{this.newPriceInputFieldIsTouched}}
        @label={{t
          "pricing_assistant.edit_price_input.label"
          currencyCode=this.hotelAndMarketService.hotel.currencyCode
          currencySymbol=this.hotelAndMarketService.hotel.currencySymbol
        }}
        @max={{NUMBER_INPUT_MAX_VALUE}}
        @min={{0}}
        @onValueChange={{this.updateNewPrice}}
        @removePadding={{true}}
        @required={{true}}
        @value={{@newPriceValue}}
        @warningMessage={{this.newPriceInputFieldWarningState.warningMessage}}
      />
    </div>
  </template>
}
